import { rateService } from '@/services/rateService'
import type { Group, User, DailyRate } from '@/types/database.types'
import type { DashboardData, AgendaWidgetData, UpcomingMeetingData } from './types'
import { calculateDashboardDisplayData } from './finance'
import { calculateNextPaymentDate, daysBetween } from './date'
import { formatNaturalDate } from './turkish'
import { calculatePotAmount, formatCurrency } from './finance'

export {
  getCachedDashboardData,
  setCachedDashboardData,
  clearDashboardCache,
  getCachedGroups,
  setCachedGroups,
  clearGroupsCache,
} from './dashboard_cache'
import {
  setCachedDashboardData,
} from './dashboard_cache'
import { logError, logWarn } from '@/lib/log'
import { calculateClosestUpcomingEvent } from './agenda'
import {
  fetchProfile,
  fetchActiveGroupMemberships,
  fetchOwnedGroups,
  fetchUserActiveTransactions,
} from '@/services/dashboardService'
import { mergeUserGroups, type GroupRow } from './ownedGroups'
import { nextReceiver } from './memberName'

/** B-26: kurulan gün sorgusunun döndürdüğü satır (dashboardService.fetchOwnedGroups). */
type OwnedGroupRow = {
  id: string
  current_cycle?: number | null
  group_members?: { turn_index?: number | null; users?: unknown }[]
}

let _profileCache: any | null = null

export function getCachedProfile(): any | null {
  if (!_profileCache && typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const persisted = localStorage.getItem('snn_profile_cache')
      if (persisted) {
        _profileCache = JSON.parse(persisted)
      }
    } catch (e) {
      logWarn('[Abacus] LocalStorage profile cache read error:', e)
    }
  }
  return _profileCache
}

export function setCachedProfile(profile: any): void {
  _profileCache = profile
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem('snn_profile_cache', JSON.stringify(profile))
    } catch (e) {
      logWarn('[Abacus] LocalStorage profile cache write error:', e)
    }
  }
}

export function clearProfileCache(): void {
  _profileCache = null
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem('snn_profile_cache')
    } catch (_e) {
      // ignore
    }
  }
}


export function getVisibleWidgets(userGroups: Group[], userPreferences: any): string[] {
  if (userPreferences?.dashboard_widgets && Array.isArray(userPreferences.dashboard_widgets)) {
    return userPreferences.dashboard_widgets
      .filter((w: any) => w.visible)
      .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
      .map((w: any) => {
        if (w.id === 'gram_gold') return 'gram_24_try'
        if (w.id === 'quarter_gold') return 'quarter_gold_try'
        if (w.id === 'usd') return 'usd_try'
        if (w.id === 'eur') return 'eur_try'
        if (w.id === 'gram_silver') return 'gram_silver_try'
        return w.id
      })
  }

  const smartWidgets = new Set<string>()
  smartWidgets.add('gram_24_try')
  smartWidgets.add('quarter_gold_try')

  if (userGroups && userGroups.length > 0) {
    userGroups.forEach(group => {
      const currency = group.currency
      if (currency === 'USD') smartWidgets.add('usd_try')
      if (currency === 'EUR') smartWidgets.add('eur_try')
      if (currency === 'GRAM_GOLD') smartWidgets.add('gram_24_try')
      if (currency === 'GRAM_SILVER') smartWidgets.add('gram_silver_try')
    })
  } else {
    smartWidgets.add('usd_try')
    smartWidgets.add('eur_try')
  }

  return Array.from(smartWidgets)
}

export function getMemberPaymentStatus(
  memberId: string,
  cycleNumber: number,
  transactions: any[],
  currentCycle: number
): 'paid' | 'pending' | 'unpaid' | 'skipped' | 'future' {
  if (cycleNumber > currentCycle) return 'future'
  const trx = transactions?.find(
    t =>
      t.sender_id === memberId &&
      t.cycle_number === cycleNumber &&
      (t.status === 'confirmed' || t.status === 'auto_confirmed' || t.status === 'pending')
  )
  if (trx) {
    if (trx.status === 'confirmed' || trx.status === 'auto_confirmed') return 'paid'
    if (trx.status === 'pending') return 'pending'
  }
  return 'unpaid'
}

export async function getDashboardData(userId: string, signal?: AbortSignal): Promise<DashboardData> {
  let currentUser: User | null = null
  let currentGroups: (Group & { member_count: number; total_cycles: number; next_member: any })[] = []
  let marketRates: any = {
    gram_24_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    gram_22_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    quarter_gold_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    half_gold_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    full_gold_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    usd_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    eur_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
    gram_silver_try: { value: '...', change: '...', trendUp: true, rawChange: 0 },
  }
  let visibleWidgetIds: string[] = ['gram_24_try', 'quarter_gold_try', 'usd_try', 'eur_try']
  let agenda: AgendaWidgetData | null = null
  let upcomingMeeting: UpcomingMeetingData | null = null
  let todayRate: DailyRate | null = null
  let userTransactions: any[] = []

  try {
    const [profileRes, groupsRes, ratesRes, ownedRes, prefsRes, userTxRes] = await Promise.allSettled([
      fetchProfile(userId, signal),
      fetchActiveGroupMemberships(userId, signal),
      rateService.getLatestTwoRates(),
      fetchOwnedGroups(userId, signal),
      import('@/services/userService').then(mod => mod.getPreferences(userId).then(r => r.data)),
      fetchUserActiveTransactions(userId, signal),
    ])

    const profileResult = profileRes.status === 'fulfilled' ? profileRes.value : null;
    
    if (profileResult?.error) {
      throw new Error(`Profile fetch error: ${profileResult.error.message}`);
    }
    if (profileRes.status === 'rejected') {
      throw new Error(`Profile fetch rejected: ${profileRes.reason}`);
    }
    if (!profileResult?.data) {
      throw new Error(`Profile fetch returned no data.`);
    }

    currentUser = profileResult.data as any;

    if (groupsRes.status === 'fulfilled' && groupsRes.value.data) {
      currentGroups = groupsRes.value.data
        .map((m: any) => {
          const groupData = m.group
          if (!groupData) return null
          const members = groupData.group_members || []
          const memberCount = members.length
          const currentCycle = groupData.current_cycle || 1
          const totalCycles = members.length || 10
          return {
            ...groupData,
            member_count: memberCount,
            total_cycles: totalCycles,
            // B-42: hesapsız sırada profil yok; ad defterden okunur.
            next_member: nextReceiver(members, currentCycle),
          }
        })
        .filter((g): g is NonNullable<typeof g> => g !== null)
    } else if (groupsRes.status === 'rejected' || (groupsRes.status === 'fulfilled' && groupsRes.value.error)) {
        logError('[Abacus] Groups fetch warning:', groupsRes);
    }

    // B-26: organizatör listede kendine sıra almadıysa kurduğu gün üyelik sorgusuna düşmez.
    if (ownedRes.status === 'fulfilled' && ownedRes.value.data) {
      const owned = (ownedRes.value.data as OwnedGroupRow[]).map(groupData => {
        const members = groupData.group_members ?? []
        const currentCycle = groupData.current_cycle ?? 1
        return {
          ...groupData,
          member_count: members.length,
          total_cycles: members.length || 10,
          next_member: nextReceiver(members as never, currentCycle),
        }
      })
      currentGroups = mergeUserGroups(
        currentGroups as unknown as GroupRow[],
        owned as unknown as GroupRow[]
      ) as unknown as typeof currentGroups
    } else if (ownedRes.status === 'rejected' || (ownedRes.status === 'fulfilled' && ownedRes.value.error)) {
      logError('[Abacus] Owned groups fetch warning:', ownedRes)
    }

    const ratesResult = ratesRes.status === 'fulfilled' ? ratesRes.value : null
    todayRate = ratesResult?.today || null
    const yesterdayRate = ratesResult?.yesterday || null
    marketRates = calculateDashboardDisplayData(todayRate, yesterdayRate)

    const prefs = prefsRes.status === 'fulfilled' ? prefsRes.value : null
    visibleWidgetIds = getVisibleWidgets(currentGroups as unknown as Group[], prefs)

    userTransactions = userTxRes.status === 'fulfilled' ? (userTxRes.value.data || []) : []
    agenda = await calculateClosestUpcomingEvent(currentGroups, userId, todayRate, userTransactions)

    if (agenda) {
      upcomingMeeting = {
        groupName: agenda.groupName,
        groupId: agenda.groupId,
        daysRemaining: agenda.daysRemaining,
        naturalDateText: agenda.naturalDateText,
        formattedMonth: agenda.formattedMonth,
        formattedDay: agenda.formattedDay,
        isSameAsPayment: true,
        locationName: '',
      }
    }
  } catch (error) {
    logError('[Abacus] getDashboardData hatası:', error)
    // SESSİZCE YUTMA: Eğer hata alınmışsa (özellikle network/auth RLS) UI'ı boşaltmamak için fırlat!
    throw error
  }

  const result: DashboardData = {
    user: currentUser,
    groups: currentGroups,
    marketRates,
    visibleWidgets: visibleWidgetIds,
    agenda,
    upcomingMeeting,
    transactions: userTransactions,
  }
  setCachedDashboardData(result)
  return result
}
